<template>
    <main class="profile-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">{{ title }}</div>
        </portal>

        <header class="profile-hero">
            <h1 class="profile-hero-title">{{ title }}</h1>
            <p v-if="subtitle" class="profile-hero-sub">{{ subtitle }}</p>
        </header>

        <div v-if="loading" class="profile-loading">
            <div class="spinner-border text-primary" role="status">
                <span class="sr-only">{{ $t('forms.loading') }}...</span>
            </div>
        </div>

        <template v-else-if="user">
            <div class="up-header">
                <div class="up-avatar">{{ getInitials(user.name) }}</div>
                <div class="up-header-info">
                    <h2 class="up-name">{{ user.name }}</h2>
                    <p v-if="user.email" class="up-email">
                        <i class="fal fa-envelope"></i> {{ user.email }}
                    </p>
                    <p v-if="user.phone" class="up-email">
                        <i class="fal fa-phone"></i> {{ user.phone }}
                    </p>
                    <div v-if="user.roles && user.roles.length" class="up-roles">
                        <span
                            v-for="role in user.roles"
                            :key="role.id || role.name"
                            class="up-role-badge"
                        >{{ role.name }}</span>
                    </div>
                </div>
                <div v-if="$slots.actions" class="up-header-actions">
                    <slot name="actions" />
                </div>
            </div>

            <slot name="edit-panel" />

            <div v-if="stats" class="up-stats">
                <div class="up-stat-card">
                    <div class="up-stat-icon up-stat-icon--cyan">
                        <i class="fal fa-plus-circle"></i>
                    </div>
                    <div class="up-stat-data">
                        <span class="up-stat-value">{{ stats.posts_created || 0 }}</span>
                        <span class="up-stat-label">{{ $t('user_profile.stats.posts_created') }}</span>
                    </div>
                </div>
                <div class="up-stat-card">
                    <div class="up-stat-icon up-stat-icon--green">
                        <i class="fal fa-user-check"></i>
                    </div>
                    <div class="up-stat-data">
                        <span class="up-stat-value">{{ stats.posts_as_operator || 0 }}</span>
                        <span class="up-stat-label">{{ $t('user_profile.stats.posts_as_operator') }}</span>
                    </div>
                </div>
                <div class="up-stat-card">
                    <div class="up-stat-icon up-stat-icon--blue">
                        <i class="fal fa-eye"></i>
                    </div>
                    <div class="up-stat-data">
                        <span class="up-stat-value">{{ stats.posts_as_informed || 0 }}</span>
                        <span class="up-stat-label">{{ $t('user_profile.stats.posts_as_informed') }}</span>
                    </div>
                </div>
                <div class="up-stat-card">
                    <div class="up-stat-icon up-stat-icon--orange">
                        <i class="fal fa-comment-alt"></i>
                    </div>
                    <div class="up-stat-data">
                        <span class="up-stat-value">{{ stats.comments_count || 0 }}</span>
                        <span class="up-stat-label">{{ $t('user_profile.stats.comments_count') }}</span>
                    </div>
                </div>
            </div>

            <NotificationPreferences
                :user-id="user.id"
                :preferences="notificationPreferences"
                style="margin-top: 20px;"
            />

            <div v-if="histories" style="margin-top: 20px;">
                <ActivityTimeline :histories="histories" />
            </div>
        </template>
    </main>
</template>

<script>
import NotificationPreferences from '@/components/UI/NotificationPreferences.vue'
import ActivityTimeline from '@/components/UI/ActivityTimeline.vue'

export default {
    name: 'ProfileView',
    components: {
        NotificationPreferences,
        ActivityTimeline
    },
    props: {
        title: { type: String, required: true },
        subtitle: { type: String, default: '' },
        loading: { type: Boolean, default: false },
        user: { type: Object, default: null },
        stats: { type: Object, default: null },
        notificationPreferences: { type: Array, default: () => [] },
        histories: { type: Array, default: null }
    },
    methods: {
        getInitials(name) {
            if (!name) return '?'
            return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
        }
    }
}
</script>

<style scoped>
.profile-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.profile-hero {
    margin-bottom: var(--v2-space-5);
}

.profile-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.profile-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
    max-width: 720px;
}

.profile-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-6);
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
}

.up-header {
    display: flex;
    align-items: center;
    gap: 20px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    padding: 22px 26px;
    flex-wrap: wrap;
}

.up-avatar {
    width: 64px;
    height: 64px;
    border-radius: var(--v2-radius-lg);
    background: linear-gradient(135deg, #0891B2, #06b6d4);
    color: #fff;
    font-size: 1.3rem;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.up-header-info {
    flex: 1;
    min-width: 0;
}

.up-name {
    font-size: 1.2rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    margin: 0 0 4px;
    letter-spacing: -0.01em;
}

.up-email {
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    margin: 0 0 8px;
}

.up-email i {
    margin-right: 4px;
    font-size: 0.75rem;
}

.up-roles {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.up-role-badge {
    display: inline-flex;
    padding: 3px 12px;
    border-radius: 999px;
    background: rgba(8, 145, 178, 0.1);
    color: var(--v2-primary);
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
    text-transform: capitalize;
}

.up-header-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
}

.up-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--v2-space-3);
    margin-top: var(--v2-space-4);
}

.up-stat-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    padding: 18px 20px;
    display: flex;
    align-items: center;
    gap: 14px;
}

.up-stat-icon {
    width: 42px;
    height: 42px;
    border-radius: var(--v2-radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.up-stat-icon i {
    font-size: 1.05rem;
}

.up-stat-icon--cyan { background: rgba(8, 145, 178, 0.1); color: #0891B2; }
.up-stat-icon--green { background: rgba(16, 185, 129, 0.1); color: #10b981; }
.up-stat-icon--blue { background: rgba(59, 130, 246, 0.1); color: #3b82f6; }
.up-stat-icon--orange { background: rgba(245, 158, 11, 0.1); color: #f59e0b; }

.up-stat-data {
    display: flex;
    flex-direction: column;
}

.up-stat-value {
    font-size: 1.4rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
}

.up-stat-label {
    font-size: 0.72rem;
    color: var(--v2-text-3);
    font-weight: var(--v2-font-weight-semibold);
}

@media (max-width: 1024px) {
    .up-stats {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 900px) {
    .profile-v2 {
        padding: 16px 18px 32px;
    }

    .profile-hero-title {
        font-size: 1.375rem;
    }
}

@media (max-width: 768px) {
    .up-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .up-stats {
        grid-template-columns: 1fr;
    }

    .up-header-actions {
        width: 100%;
    }
}
</style>
